import type { Tournament } from '../../types';

export type TournamentDiscoveryMode = 'all' | 'joinable' | 'live' | 'upcoming';

export type TournamentDiscoveryFilters = {
  mode: TournamentDiscoveryMode;
  freeOnly: boolean;
  hideUnaffordable: boolean;
};

export type TournamentMapPoint = { lat: number; lng: number };


export function isWorldwideTournament(tournament: Tournament | any): boolean {
  const locationScope = String(tournament?.locationScope || '').trim().toLowerCase();
  const tournamentScale = String(tournament?.tournamentScale || '').trim().toLowerCase();
  const locationLabel = String(
    tournament?.locationLabel || tournament?.mapLocation?.label || '',
  ).trim().toLowerCase();

  return (
    locationScope === 'worldwide' ||
    tournamentScale === 'open_global' ||
    tournamentScale === 'global' ||
    locationLabel === 'worldwide' ||
    locationLabel === 'global'
  );
}

export const DEFAULT_TOURNAMENT_DISCOVERY_FILTERS: TournamentDiscoveryFilters = {
  mode: 'all',
  freeOnly: false,
  hideUnaffordable: false,
};

export type TournamentDiscoveryPreferences = {
  filters: TournamentDiscoveryFilters;
  hideAllMapTournaments: boolean;
};

export const TOURNAMENT_DISCOVERY_PREFS_KEY = 'roddo.tournaments.discoveryPrefs.v1';
export const TOURNAMENT_DISCOVERY_PREFS_EVENT = 'roddo:tournament-discovery-prefs-changed';

export const DEFAULT_TOURNAMENT_DISCOVERY_PREFERENCES: TournamentDiscoveryPreferences = {
  filters: DEFAULT_TOURNAMENT_DISCOVERY_FILTERS,
  hideAllMapTournaments: false,
};

function normalizeTournamentDiscoveryFilters(value: any): TournamentDiscoveryFilters {
  const rawMode = String(value?.mode || 'all');
  const mode: TournamentDiscoveryMode = ['all', 'joinable', 'live', 'upcoming'].includes(rawMode)
    ? (rawMode as TournamentDiscoveryMode)
    : 'all';

  return {
    mode,
    freeOnly: value?.freeOnly === true,
    hideUnaffordable: value?.hideUnaffordable === true,
  };
}

export function loadTournamentDiscoveryPreferences(): TournamentDiscoveryPreferences {
  try {
    if (typeof window === 'undefined') return DEFAULT_TOURNAMENT_DISCOVERY_PREFERENCES;
    const raw = window.localStorage.getItem(TOURNAMENT_DISCOVERY_PREFS_KEY);
    if (!raw) return DEFAULT_TOURNAMENT_DISCOVERY_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      filters: normalizeTournamentDiscoveryFilters(parsed?.filters),
      hideAllMapTournaments: parsed?.hideAllMapTournaments === true,
    };
  } catch {
    return DEFAULT_TOURNAMENT_DISCOVERY_PREFERENCES;
  }
}

export function saveTournamentDiscoveryPreferences(
  patch: Partial<TournamentDiscoveryPreferences>,
): TournamentDiscoveryPreferences {
  const current = loadTournamentDiscoveryPreferences();
  const next: TournamentDiscoveryPreferences = {
    filters: patch.filters
      ? normalizeTournamentDiscoveryFilters(patch.filters)
      : current.filters,
    hideAllMapTournaments:
      typeof patch.hideAllMapTournaments === 'boolean'
        ? patch.hideAllMapTournaments
        : current.hideAllMapTournaments,
  };

  try {
    if (typeof window !== 'undefined') {
      const nextJson = JSON.stringify(next);
      const previousJson = window.localStorage.getItem(TOURNAMENT_DISCOVERY_PREFS_KEY);
      if (previousJson !== nextJson) {
        window.localStorage.setItem(TOURNAMENT_DISCOVERY_PREFS_KEY, nextJson);
        window.dispatchEvent(
          new CustomEvent(TOURNAMENT_DISCOVERY_PREFS_EVENT, { detail: next }),
        );
      }
    }
  } catch {}

  return next;
}

export function getTournamentMapLatLng(tournament: Tournament | any): TournamentMapPoint | null {
  const lat = Number(
    tournament?.mapLocation?.lat ??
      tournament?.mapCoords?.lat ??
      tournament?.position?.lat,
  );
  const lng = Number(
    tournament?.mapLocation?.lng ??
      tournament?.mapLocation?.lon ??
      tournament?.mapCoords?.lng ??
      tournament?.mapCoords?.lon ??
      tournament?.position?.lng ??
      tournament?.position?.lon,
  );

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return { lat, lng };
}

export function isTournamentLive(tournament: Tournament | any) {
  const status = String(tournament?.status || '').toLowerCase();
  return status === 'active' || status === 'live';
}

export function isTournamentJoinable(tournament: Tournament | any, now = Date.now()) {
  const status = String(tournament?.status || '').toLowerCase();
  const closesAt = Number(tournament?.registrationClosesAt || 0);
  return status === 'registration_open' && (!closesAt || closesAt > now);
}

export function isTournamentUpcoming(tournament: Tournament | any, now = Date.now()) {
  const status = String(tournament?.status || '').toLowerCase();
  const startDate = Number(tournament?.startDate || 0);
  return status === 'approved' && (!startDate || startDate > now);
}

/**
 * Public tournament eligibility for the Tournament Dock list.
 *
 * IMPORTANT:
 * A tournament does NOT need a Big Blue pin to be listable/discoverable.
 * Worldwide/no-pin events are valid public tournaments and must remain visible.
 */
export function isTournamentPubliclyListable(
  tournament: Tournament | any,
  now = Date.now(),
) {
  const status = String(tournament?.status || '').toLowerCase();
  const visibility = String(tournament?.visibility || '').toLowerCase();
  const endDate = Number(tournament?.endDate || 0);

  if (visibility !== 'public') return false;
  if (!['approved', 'registration_open', 'active', 'live'].includes(status)) return false;
  if (endDate > 0 && endDate <= now) return false;

  return true;
}

/**
 * Big Blue map eligibility.
 *
 * Map markers require a valid public map point, but the Tournament Dock list does not.
 */
export function isTournamentDiscoverableNow(
  tournament: Tournament | any,
  now = Date.now(),
) {
  if (!isTournamentPubliclyListable(tournament, now)) return false;
  return isWorldwideTournament(tournament) || !!getTournamentMapLatLng(tournament);
}

export function tournamentPassesDiscoveryFilters(
  tournament: Tournament | any,
  filters: TournamentDiscoveryFilters,
  shellBalance: number,
  now = Date.now(),
) {
  // List filtering must never require a map pin.
  if (!isTournamentPubliclyListable(tournament, now)) return false;

  if (filters.mode === 'joinable' && !isTournamentJoinable(tournament, now)) return false;
  if (filters.mode === 'live' && !isTournamentLive(tournament)) return false;
  if (filters.mode === 'upcoming' && !isTournamentUpcoming(tournament, now)) return false;

  const fee = Math.max(0, Math.floor(Number(tournament?.entryFeeShells || 0)));
  if (filters.freeOnly && fee > 0) return false;
  if (filters.hideUnaffordable && fee > Math.max(0, Math.floor(Number(shellBalance || 0)))) {
    return false;
  }

  return true;
}

export function getTournamentDiscoveryPriority(tournament: Tournament | any) {
  if (isTournamentLive(tournament)) return 3;
  if (isTournamentJoinable(tournament)) return 2;
  return 1;
}
